import type { ReactNode, MouseEvent as ReactMouseEvent } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { HueGlowCard } from "@/components/ui/hue-glow-card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, ChevronRight } from "lucide-react";
import { getInitials } from "@/lib/get-initials";
import { useIsMobile } from "@/hooks/use-mobile";

export interface EntityCardUser {
  name: string;
  avatarUrl?: string | null;
}

export interface EntityCardProps {
  href: string;
  hueColor: string;
  /** Icon element — rendered top-left in a colored container */
  icon: ReactNode;
  title: string;
  /** Optional subtitle/description — two-line clamp */
  subtitle?: string | null | undefined;
  /** Badge rendered next to the title */
  badge?: ReactNode;
  /** Creator/owner avatar shown top-right */
  user?: EntityCardUser | null | undefined;
  /** Left-side metadata text (e.g. "5 features") */
  meta?: string | undefined;
  /** Right-side secondary meta (e.g. "Engineering") */
  secondaryMeta?: string | undefined;
  /** Progress 0–100 for a colored bar */
  progress?: number | undefined;
  /** Bottom row content — e.g. member avatars */
  footer?: ReactNode;
  /** Dropdown menu items */
  menuItems?: ReactNode;
  className?: string;
  dimmed?: boolean | undefined;
}

/** Fixed-height card used for teams, epics, and projects. */
export function EntityCard({
  href,
  hueColor,
  icon,
  title,
  subtitle,
  badge,
  user,
  meta,
  secondaryMeta,
  progress,
  footer,
  menuItems,
  className,
  dimmed,
}: EntityCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <HueGlowCard
        hueColor={hueColor}
        className={`hover:bg-white/[0.04] active:bg-white/[0.06] transition-colors cursor-pointer group ${dimmed ? "opacity-50" : ""} ${className ?? ""}`}
      >
        <Link to={href} className="flex items-center gap-3 p-3.5 min-h-[56px]">
          {/* Compact icon */}
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl shrink-0"
            style={{ backgroundColor: `${hueColor}15` }}
          >
            {icon}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-[15px] font-semibold truncate leading-snug">{title}</h3>
              {badge}
            </div>
            {subtitle && (
              <p className="text-[13px] text-muted-foreground truncate mt-0.5">{subtitle}</p>
            )}
            {/* Meta + progress inline */}
            <div className="flex items-center gap-3 mt-1.5">
              {(meta ?? secondaryMeta) && (
                <span className="text-[12px] text-muted-foreground shrink-0">
                  {meta}{secondaryMeta ? ` · ${secondaryMeta}` : ''}
                </span>
              )}
              {progress != null && (
                <div className="flex-1 h-1 rounded-full bg-white/[0.04] overflow-hidden max-w-[80px]">
                  <div
                    className="h-full rounded-full bg-white/[0.12] group-hover:bg-white/70 transition-colors duration-300"
                    style={{ width: `${String(Math.min(100, Math.max(0, progress)))}%` }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Avatar */}
          {user && (
            <Avatar className="h-7 w-7 border-2 border-white/[0.08] shrink-0">
              {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
              <AvatarFallback className="text-[10px] bg-white/[0.06] font-medium">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
          )}

          {/* Drill-down chevron */}
          <ChevronRight className="h-4 w-4 text-muted-foreground/30 shrink-0 -mr-1" />
        </Link>
      </HueGlowCard>
    );
  }

  return (
    <HueGlowCard
      hueColor={hueColor}
      className={`hover:bg-white/[0.04] active:bg-white/[0.06] transition-colors cursor-pointer group ${dimmed ? "opacity-50" : ""} ${className ?? ""}`}
    >
      <Link to={href} className="flex flex-col h-[210px] p-5">
        {/* Top row: icon left — avatar top-right */}
        <div className="flex items-start justify-between">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl shrink-0"
            style={{ backgroundColor: `${hueColor}15` }}
          >
            {icon}
          </div>

          {/* Avatar slides left on hover to make room for ellipsis */}
          {user && (
            <Avatar className={`h-8 w-8 border-2 border-white/[0.08] ${menuItems ? "transition-transform duration-300 ease-out group-hover:-translate-x-8" : ""}`}>
              {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
              <AvatarFallback className="text-xs bg-white/[0.06] font-medium">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
          )}
        </div>

        {/* Title + badge */}
        <div className="mt-3.5 flex items-start gap-2">
          <h3 className="text-[15px] font-semibold leading-snug truncate">{title}</h3>
          {badge}
        </div>

        {/* Subtitle */}
        {subtitle && (
          <p className="text-xs text-muted-foreground truncate mt-1">{subtitle}</p>
        )}

        {/* Spacer pushes bottom content down */}
        <div className="flex-1" />

        {/* Meta row (dates, counts) */}
        {(meta ?? secondaryMeta) && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            {meta && <span>{meta}</span>}
            {secondaryMeta && <span>{secondaryMeta}</span>}
          </div>
        )}

        {/* Progress bar — subtle grey, fill turns white on hover */}
        {progress != null && (
          <div className="mt-2 h-1.5 w-full rounded-full bg-white/[0.04] overflow-hidden">
            <div
              className="h-full rounded-full bg-white/[0.12] group-hover:bg-white/70 transition-colors duration-300"
              style={{ width: `${String(Math.min(100, Math.max(0, progress)))}%` }}
            />
          </div>
        )}

        {/* Footer row */}
        {footer && <div className="mt-2.5">{footer}</div>}
      </Link>

      {/* Dropdown menu — outside Link so click works */}
      {menuItems && (
        <div className={`absolute top-5 right-5 transition-opacity duration-300 ease-out ${menuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
          <DropdownMenu onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 md:h-8 md:w-8"
                onClick={(e: ReactMouseEvent) => { e.stopPropagation(); }}
              >
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {menuItems}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

    </HueGlowCard>
  );
}
